import React, { useEffect, useMemo, useState } from 'react'
import Navbar from '../components/Navbar'
import Footer from '../components/Footer'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faBriefcase,
  faCircleCheck,
  faEye,
  faFileUpload,
  faGraduationCap,
  faPlus,
  faSave,
  faScrewdriverWrench,
  faReceipt,
  faUserMinus,
  faUserTie,
} from '@fortawesome/free-solid-svg-icons'
import { useAuth } from '../contexts/AuthContext'
import { useDocumentViewer } from '../contexts/DocumentViewerContext'
import DaisyDatePicker from '../components/DaisyDatePicker'
import HrToolsTab from '../components/hr/HrToolsTab'
import HrAdvancesTab from '../components/hr/HrAdvancesTab'
import HrPayrollConfigCard from '../components/hr/HrPayrollConfigCard'
import HrPaylocityTimeExportCard, { HR_SELECT_EMPLOYEE_EVENT } from '../components/hr/HrPaylocityTimeExportCard'
import HrEeapCategoriesCard from '../components/hr/HrEeapCategoriesCard'
import HrCertTypesCard from '../components/hr/HrCertTypesCard'
import HrPositionsCard from '../components/hr/HrPositionsCard'
import HrAdvanceCategoriesCard from '../components/hr/HrAdvanceCategoriesCard'
import HrEmployeeDirectory from '../components/hr/HrEmployeeDirectory'
import PositionSelect from '../components/PositionSelect'
import { hrToolsService } from '../services/hrToolsService'
import {
  certDisplayLabel,
  certLabel,
  profileService,
  type CertTypeDef,
  type ContactEntry,
  type EmployeeCert,
  type EmployeeProfile,
} from '../services/profileService'

interface HRPageProps {
}

type HRTab = 'employeeManagement' | 'certManager' | 'payrollTools' | 'hrTools' | 'advances'
type CertDraft = { expirationDate: string; inProgress: boolean }
type CertSaveIndicator = { kind: 'idle' | 'saving' | 'saved' | 'error'; message: string }
type DriverStatusValue = 'valid' | 'invalid' | 'pending'
type LicenseTypeValue = 'cdl' | 'non-cdl' | 'none'
type PayTypeValue = 'hourly' | 'salary'
type DepartmentValue = 'Field' | 'Office'

const DRIVER_STATUS_OPTIONS: Array<{ value: DriverStatusValue; label: string }> = [
  { value: 'valid', label: 'Valid' },
  { value: 'invalid', label: 'Invalid' },
  { value: 'pending', label: 'Pending' },
]

const LICENSE_TYPE_OPTIONS: Array<{ value: LicenseTypeValue; label: string }> = [
  { value: 'cdl', label: 'CDL' },
  { value: 'non-cdl', label: 'Non-CDL' },
  { value: 'none', label: 'None' },
]

const PAY_TYPE_OPTIONS: Array<{ value: PayTypeValue; label: string }> = [
  { value: 'hourly', label: 'Hourly' },
  { value: 'salary', label: 'Salary' },
]

const DEPARTMENT_OPTIONS: Array<{ value: DepartmentValue; label: string }> = [
  { value: 'Field', label: 'Field' },
  { value: 'Office', label: 'Office' },
]

function splitName(fullName?: string | null) {
  const parts = String(fullName || '').trim().split(/\s+/).filter(Boolean)
  if (!parts.length) return { firstName: '', lastName: '' }
  return {
    firstName: parts[0],
    lastName: parts.slice(1).join(' '),
  }
}

function normalizeDriverStatus(value?: string | null): DriverStatusValue | '' {
  const raw = String(value || '').trim().toLowerCase()
  if (!raw) return ''
  if (raw === 'valid' || raw === 'good') return 'valid'
  if (raw === 'invalid' || raw === 'bad') return 'invalid'
  if (raw === 'pending' || raw === 'waiting' || raw === 'inprogress' || raw === 'in-progress') return 'pending'
  return ''
}

function normalizeLicenseType(value?: string | null): LicenseTypeValue | '' {
  const raw = String(value || '').trim().toLowerCase()
  if (!raw) return 'none'
  if (raw === 'none' || raw === 'n/a' || raw === 'na') return 'none'
  if (raw === 'cdl' || raw === 'class a' || raw === 'class b' || raw === 'class c') return 'cdl'
  if (raw === 'non-cdl' || raw === 'non cdl' || raw === 'standard' || raw === 'class d' || raw === 'regular') return 'non-cdl'
  return ''
}

function normalizePayType(value?: string | null): PayTypeValue | '' {
  const raw = String(value || '').trim().toLowerCase()
  if (raw === 'salary' || raw === 'salaried') return 'salary'
  if (raw === 'hourly' || raw === 'hour') return 'hourly'
  return ''
}

function normalizeDepartment(value?: string | null): DepartmentValue | '' {
  const raw = String(value || '').trim().toLowerCase()
  if (raw === 'field') return 'Field'
  if (raw === 'office') return 'Office'
  return ''
}

function normalizeEmail(value?: string | null) {
  return String(value || '').trim().toLowerCase()
}

function formatPhoneInput(value?: string | null) {
  const digits = String(value || '').replace(/\D/g, '').slice(0, 10)
  if (!digits) return ''
  if (digits.length <= 3) return `(${digits}`
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
}

function formatZipInput(value?: string | null) {
  const digits = String(value || '').replace(/\D/g, '').slice(0, 9)
  if (!digits) return ''
  if (digits.length <= 5) return digits
  return `${digits.slice(0, 5)}-${digits.slice(5)}`
}

function buildCertDrafts(rows: EmployeeCert[]) {
  const drafts: Record<string, CertDraft> = {}
  rows.forEach((cert) => {
    drafts[cert.type] = {
      expirationDate: cert.expirationDate ? cert.expirationDate.slice(0, 10) : '',
      inProgress: cert.status === 'inProgress',
    }
  })
  return drafts
}

function autoStatusFromExpiration(expirationDate: string | null | undefined): 'good' | 'bad' {
  if (!expirationDate) return 'bad'
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const expDate = new Date(expirationDate)
  expDate.setHours(0, 0, 0, 0)
  return expDate >= today ? 'good' : 'bad'
}

function statusFromDraft(draft: CertDraft): EmployeeCert['status'] {
  if (draft.inProgress) return 'inProgress'
  return autoStatusFromExpiration(draft.expirationDate || null)
}

function isCertDraftDirty(cert: EmployeeCert, draft?: CertDraft) {
  if (!draft) return false
  const savedDate = cert.expirationDate ? cert.expirationDate.slice(0, 10) : ''
  const savedInProgress = cert.status === 'inProgress'
  return draft.expirationDate !== savedDate || draft.inProgress !== savedInProgress
}

function mergePreservedCertDrafts(
  rows: EmployeeCert[],
  previous: Record<string, CertDraft>,
) {
  const next = buildCertDrafts(rows)
  for (const cert of rows) {
    const draft = previous[cert.type]
    if (isCertDraftDirty(cert, draft) && draft) next[cert.type] = draft
  }
  return next
}

function statusBadgeClass(status: EmployeeCert['status']) {
  if (status === 'good') return 'badge-success'
  if (status === 'bad') return 'badge-error'
  return 'badge-warning'
}

function statusLabel(status: EmployeeCert['status']) {
  if (status === 'good') return 'Complete'
  if (status === 'bad') return 'Expired'
  return 'In Progress'
}

const HRPage: React.FC<HRPageProps> = () => {
  const { hasPermission } = useAuth()
  const { openDocument } = useDocumentViewer()
  const [activeTab, setActiveTab] = useState<HRTab>('employeeManagement')

  const [searchTerm, setSearchTerm] = useState('')
  const [employeeDirectory, setEmployeeDirectory] = useState<EmployeeProfile[]>([])
  const [directoryLoading, setDirectoryLoading] = useState(false)
  const [directoryError, setDirectoryError] = useState<string | null>(null)

  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string>('')
  const [configuredPositions, setConfiguredPositions] = useState<string[]>([])
  const [profileForm, setProfileForm] = useState<EmployeeProfile | null>(null)
  const [contactForm, setContactForm] = useState<ContactEntry | null>(null)
  const [detailsLoading, setDetailsLoading] = useState(false)
  const [detailsError, setDetailsError] = useState<string | null>(null)
  const [savingEmployee, setSavingEmployee] = useState(false)
  const [employeeMessage, setEmployeeMessage] = useState<string | null>(null)

  const [certs, setCerts] = useState<EmployeeCert[]>([])
  const [certDrafts, setCertDrafts] = useState<Record<string, CertDraft>>({})
  const [certsLoading, setCertsLoading] = useState(false)
  const [certsError, setCertsError] = useState<string | null>(null)
  const [certsSaving, setCertsSaving] = useState(false)
  const [certUploadingType, setCertUploadingType] = useState<string | null>(null)
  const [certSaveIndicator, setCertSaveIndicator] = useState<CertSaveIndicator>({ kind: 'idle', message: '' })
  const [certTypeToAssign, setCertTypeToAssign] = useState<string>('')
  const [certCatalog, setCertCatalog] = useState<CertTypeDef[]>([])

  const canUseHr = hasPermission('hrManager')

  useEffect(() => {
    if (!canUseHr || activeTab !== 'employeeManagement') return
    let cancelled = false
    hrToolsService.getPositions()
      .then((list) => { if (!cancelled) setConfiguredPositions(list) })
      .catch(() => { if (!cancelled) setConfiguredPositions([]) })
    return () => { cancelled = true }
  }, [canUseHr, activeTab])

  useEffect(() => {
    if (!canUseHr || activeTab !== 'certManager') return
    let cancelled = false
    profileService
      .listCertTypes()
      .then((types) => {
        if (!cancelled) setCertCatalog(types)
      })
      .catch(() => {
        if (!cancelled) setCertCatalog([])
      })
    return () => {
      cancelled = true
    }
  }, [canUseHr, activeTab])

  useEffect(() => {
    if (!canUseHr) return
    let cancelled = false
    setDirectoryLoading(true)
    setDirectoryError(null)
    profileService
      .searchProfiles('')
      .then((data) => {
        if (!cancelled) setEmployeeDirectory(Array.isArray(data) ? data : [])
      })
      .catch((err: any) => {
        if (!cancelled) setDirectoryError(err?.message || 'Failed to load employees')
      })
      .finally(() => {
        if (!cancelled) setDirectoryLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [canUseHr])

  useEffect(() => {
    if (!canUseHr || !selectedEmployeeId) return
    setDetailsLoading(true)
    setDetailsError(null)
    setEmployeeMessage(null)
    Promise.all([
      profileService.getProfileByEmployeeId(selectedEmployeeId),
      profileService.getContactByEmployeeId(selectedEmployeeId),
    ])
      .then(([profile, contact]) => {
        const parsed = splitName(contact?.name)
        setProfileForm({
          ...profile,
          firstName: profile.firstName || parsed.firstName,
          lastName: profile.lastName || parsed.lastName,
          preferredName: profile.preferredName || contact.preferredName || '',
          position: profile.position || contact.position || '',
          department: normalizeDepartment(profile.department || contact.department),
          supervisor: profile.supervisor || contact.supervisor || '',
          driverStatus: normalizeDriverStatus(profile.driverStatus),
          driversLicenseType: normalizeLicenseType(profile.driversLicenseType) || 'none',
          zipCode: formatZipInput(profile.zipCode),
          payType: normalizePayType(profile.payType),
        })
        setContactForm({
          ...contact,
          email: normalizeEmail(contact.email),
          workEmail: normalizeEmail(contact.workEmail),
          phone: formatPhoneInput(contact.phone),
          workPhone: formatPhoneInput(contact.workPhone),
        })
      })
      .catch((err: any) => setDetailsError(err?.message || 'Failed to load employee details'))
      .finally(() => setDetailsLoading(false))
  }, [selectedEmployeeId, canUseHr])

  useEffect(() => {
    if (!canUseHr || !selectedEmployeeId) return
    setCertsLoading(true)
    setCertsError(null)
    setCertSaveIndicator({ kind: 'idle', message: '' })
    profileService
      .getCertsByEmployeeId(selectedEmployeeId)
      .then((rows) => {
        setCerts(rows)
        setCertDrafts(buildCertDrafts(rows))
      })
      .catch((err: any) => {
        setCertsError(err?.message || 'Failed to load certifications')
        setCerts([])
      })
      .finally(() => setCertsLoading(false))
  }, [selectedEmployeeId, canUseHr])

  const selectedEmployeeName = useMemo(() => {
    if (!profileForm) return selectedEmployeeId || 'Employee'
    const first = profileForm.preferredName || profileForm.firstName || ''
    const last = profileForm.lastName || ''
    const label = `${first} ${last}`.trim()
    return label || profileForm.employeeId
  }, [profileForm, selectedEmployeeId])

  const certRows = useMemo(() => {
    return [...certs].sort((a, b) =>
      certDisplayLabel(a).localeCompare(certDisplayLabel(b)),
    )
  }, [certs])

  const unassignedCertTypes = useMemo(() => {
    const assigned = new Set(certs.map((cert) => cert.type))
    return certCatalog
      .filter((item) => !assigned.has(item.key))
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [certs, certCatalog])

  const dirtyCerts = useMemo(
    () => certs.filter((cert) => isCertDraftDirty(cert, certDrafts[cert.type])),
    [certs, certDrafts],
  )
  const hasDirtyCerts = dirtyCerts.length > 0

  const applyCertRows = (rows: EmployeeCert[], preserveDirty = false) => {
    setCerts(rows)
    setCertDrafts((prev) => (preserveDirty ? mergePreservedCertDrafts(rows, prev) : buildCertDrafts(rows)))
  }

  const confirmDiscardCertDrafts = () => {
    if (!hasDirtyCerts) return true
    return window.confirm('You have unsaved certification changes. Discard them?')
  }

  const selectEmployee = (employeeId: string) => {
    if (employeeId === selectedEmployeeId) return
    if (!confirmDiscardCertDrafts()) return
    setSelectedEmployeeId(employeeId)
  }

  useEffect(() => {
    const onSelect = (event: Event) => {
      const employeeId = String((event as CustomEvent<{ employeeId?: string }>).detail?.employeeId || '').trim()
      if (!employeeId) return
      if (!confirmDiscardCertDrafts()) return
      setActiveTab('employeeManagement')
      setSelectedEmployeeId(employeeId)
    }
    window.addEventListener(HR_SELECT_EMPLOYEE_EVENT, onSelect)
    return () => window.removeEventListener(HR_SELECT_EMPLOYEE_EVENT, onSelect)
  }, [hasDirtyCerts])

  const setProfileField = (field: keyof EmployeeProfile, value: string) => {
    setProfileForm((prev) => (prev ? { ...prev, [field]: value } : prev))
  }

  const setContactField = (field: keyof ContactEntry, value: string) => {
    setContactForm((prev) => (prev ? { ...prev, [field]: value } : prev))
  }

  const handleSaveEmployee = async () => {
    if (!selectedEmployeeId || !profileForm || !contactForm) return
    setSavingEmployee(true)
    setEmployeeMessage(null)
    setDetailsError(null)
    try {
      await profileService.updateProfileByEmployeeId(selectedEmployeeId, {
        firstName: profileForm.firstName || '',
        lastName: profileForm.lastName || '',
        preferredName: profileForm.preferredName || '',
        position: profileForm.position || '',
        department: normalizeDepartment(profileForm.department) || '',
        supervisor: profileForm.supervisor || '',
        hireDate: profileForm.hireDate || null,
        driverStatus: normalizeDriverStatus(profileForm.driverStatus) || null,
        driversLicenseExpirationDate: profileForm.driversLicenseExpirationDate || null,
        driversLicenseType: normalizeLicenseType(profileForm.driversLicenseType) || 'none',
        dotMedicalCardExpirationDate: profileForm.dotMedicalCardExpirationDate || null,
        zipCode: formatZipInput(profileForm.zipCode) || null,
        payType: normalizePayType(profileForm.payType) || null,
      })

      await profileService.updateContactByEmployeeId(selectedEmployeeId, {
        name: contactForm.name || '',
        preferredName: contactForm.preferredName || '',
        position: profileForm.position || '',
        email: normalizeEmail(contactForm.email),
        phone: formatPhoneInput(contactForm.phone),
        workEmail: normalizeEmail(contactForm.workEmail),
        workPhone: formatPhoneInput(contactForm.workPhone),
        department: normalizeDepartment(profileForm.department) || '',
        supervisor: profileForm.supervisor || '',
      })

      setEmployeeDirectory((prev) =>
        prev.map((row) =>
          row.employeeId === selectedEmployeeId
            ? {
                ...row,
                firstName: profileForm.firstName || '',
                lastName: profileForm.lastName || '',
                preferredName: profileForm.preferredName || '',
                position: profileForm.position || '',
                department: normalizeDepartment(profileForm.department) || '',
                supervisor: profileForm.supervisor || '',
                name: contactForm.name || '',
              }
            : row,
        ),
      )
      setEmployeeMessage('Employee record saved successfully.')
    } catch (err: any) {
      setDetailsError(err?.message || 'Failed to save employee record')
    } finally {
      setSavingEmployee(false)
    }
  }

  const updateCertDraft = (
    certType: string,
    patch: Partial<CertDraft>,
  ) => {
    setCertDrafts((prev) => ({
      ...prev,
      [certType]: {
        expirationDate: prev[certType]?.expirationDate || '',
        inProgress: prev[certType]?.inProgress || false,
        ...patch,
      },
    }))
  }

  const handleSaveCerts = async () => {
    if (!selectedEmployeeId || dirtyCerts.length === 0) return
    setCertsSaving(true)
    setCertSaveIndicator({ kind: 'saving', message: 'Saving...' })
    setCertsError(null)
    try {
      const rows = await profileService.updateCerts(
        selectedEmployeeId,
        dirtyCerts.map((cert) => {
          const draft = certDrafts[cert.type] || { expirationDate: '', inProgress: false }
          return {
            certType: cert.type,
            expirationDate: draft.expirationDate || null,
            status: statusFromDraft(draft),
          }
        }),
      )
      applyCertRows(rows)
      setCertSaveIndicator({
        kind: 'saved',
        message: dirtyCerts.length === 1
          ? `${certLabel(dirtyCerts[0].type, certCatalog)} saved`
          : `${dirtyCerts.length} certifications saved`,
      })
    } catch (err: any) {
      setCertsError(err?.message || 'Failed to update certifications')
      setCertSaveIndicator({ kind: 'error', message: 'Save failed' })
    } finally {
      setCertsSaving(false)
    }
  }

  const handleUploadCert = async (certType: string, file: File) => {
    if (!selectedEmployeeId) return
    setCertUploadingType(certType)
    setCertSaveIndicator({ kind: 'saving', message: 'Uploading...' })
    setCertsError(null)
    try {
      await profileService.uploadCertFile(selectedEmployeeId, certType, file)
      const rows = await profileService.getCertsByEmployeeId(selectedEmployeeId)
      applyCertRows(rows, true)
      setCertSaveIndicator({ kind: 'saved', message: `${certLabel(certType, certCatalog)} uploaded` })
    } catch (err: any) {
      setCertsError(err?.message || 'Failed to upload certification file')
      setCertSaveIndicator({ kind: 'error', message: 'Upload failed' })
    } finally {
      setCertUploadingType(null)
    }
  }

  const handleViewCert = async (certType: string) => {
    if (!selectedEmployeeId) return
    const title = certLabel(certType, certCatalog)
    openDocument(title, 'PDF')
    try {
      const blobUrl = await profileService.getCertFileBlobUrl(selectedEmployeeId, certType)
      openDocument(title, 'PDF', blobUrl)
    } catch (err: any) {
      const message = err?.message || 'Failed to open certification file'
      setCertsError(message)
      openDocument(title, 'PDF', null, message)
    }
  }

  const handleDeleteCert = async (certType: string) => {
    if (!selectedEmployeeId) return
    if (!window.confirm(`Remove ${certLabel(certType, certCatalog)} from this employee?`)) return
    setCertSaveIndicator({ kind: 'saving', message: 'Removing...' })
    setCertsError(null)
    try {
      await profileService.deleteCert(selectedEmployeeId, certType)
      const rows = await profileService.getCertsByEmployeeId(selectedEmployeeId)
      applyCertRows(rows, true)
      setCertSaveIndicator({ kind: 'saved', message: `${certLabel(certType, certCatalog)} removed` })
    } catch (err: any) {
      setCertsError(err?.message || 'Failed to remove certification')
      setCertSaveIndicator({ kind: 'error', message: 'Remove failed' })
    }
  }

  const handleAssignCert = async () => {
    if (!selectedEmployeeId || !certTypeToAssign) return
    const certType = certTypeToAssign
    setCertSaveIndicator({ kind: 'saving', message: 'Assigning...' })
    setCertsError(null)
    try {
      await profileService.assignCertToEmployee(selectedEmployeeId, certType)
      const rows = await profileService.getCertsByEmployeeId(selectedEmployeeId)
      applyCertRows(rows, true)
      setCertTypeToAssign('')
      setCertSaveIndicator({ kind: 'saved', message: `${certLabel(certType, certCatalog)} assigned` })
    } catch (err: any) {
      setCertsError(err?.message || 'Failed to assign certification')
      setCertSaveIndicator({ kind: 'error', message: 'Assign failed' })
    }
  }

  useEffect(() => {
    if (certSaveIndicator.kind !== 'saved') return
    const timer = setTimeout(() => setCertSaveIndicator({ kind: 'idle', message: '' }), 2000)
    return () => clearTimeout(timer)
  }, [certSaveIndicator])

  const renderEmployeeDirectory = () => (
    <HrEmployeeDirectory
      employees={employeeDirectory}
      loading={directoryLoading}
      error={directoryError}
      searchTerm={searchTerm}
      onSearchTermChange={setSearchTerm}
      selectedEmployeeId={selectedEmployeeId}
      onSelect={selectEmployee}
    />
  )

  const news = [
    'HR manager tools live here',
    'Search and edit employee records',
    'Manage certifications from Cert Manager',
  ]
  const recentActivity = [
    'HR app available on dashboard',
    'Profile remains self-service',
    'Paylocity time export lives in Payroll Tools',
  ]

  const fillViewport =
    activeTab === 'advances' || activeTab === 'employeeManagement' || activeTab === 'certManager'

  return (
    <div className={`${fillViewport ? 'h-[100dvh] overflow-hidden' : 'min-h-screen'} bg-base-100 flex flex-col`}>
      <div className="shrink-0">
        <Navbar pageTitle="HR" news={news} recentActivity={recentActivity} />
      </div>
      <main className={`container mx-auto px-4 ${activeTab === 'employeeManagement' || activeTab === 'certManager' ? 'py-4' : 'py-8'} flex-1 ${fillViewport ? 'min-h-0 flex flex-col overflow-hidden' : ''}`}>
        {!canUseHr && (
          <div className="alert alert-error mb-6 shrink-0">
            <span>Your account requires the `hrManager` permission to use HR capabilities.</span>
          </div>
        )}

        <div role="tablist" className={`tabs tabs-lift mb-6 ${fillViewport ? 'shrink-0' : ''}`}>
          <button
            role="tab"
            className={`tab ${activeTab === 'employeeManagement' ? 'tab-active' : ''}`}
            onClick={() => setActiveTab('employeeManagement')}
          >
            <FontAwesomeIcon icon={faUserTie} className="mr-2" />
            Employee Management
          </button>
          <button
            role="tab"
            className={`tab ${activeTab === 'certManager' ? 'tab-active' : ''}`}
            onClick={() => setActiveTab('certManager')}
          >
            <FontAwesomeIcon icon={faGraduationCap} className="mr-2" />
            Cert Manager
          </button>
          <button
            role="tab"
            className={`tab ${activeTab === 'payrollTools' ? 'tab-active' : ''}`}
            onClick={() => setActiveTab('payrollTools')}
          >
            <FontAwesomeIcon icon={faBriefcase} className="mr-2" />
            Payroll Tools
          </button>
          <button
            role="tab"
            className={`tab ${activeTab === 'advances' ? 'tab-active' : ''}`}
            onClick={() => setActiveTab('advances')}
          >
            <FontAwesomeIcon icon={faReceipt} className="mr-2" />
            Advances
          </button>
          <button
            role="tab"
            className={`tab ${activeTab === 'hrTools' ? 'tab-active' : ''}`}
            onClick={() => setActiveTab('hrTools')}
          >
            <FontAwesomeIcon icon={faScrewdriverWrench} className="mr-2" />
            HR Tools
          </button>
        </div>

        {activeTab === 'employeeManagement' && (
          <div className="flex min-h-0 flex-1 flex-col lg:flex-row gap-4 overflow-hidden">
            <div className="h-[min(55vh,32rem)] min-h-[18rem] lg:h-auto lg:min-h-0 lg:w-[22rem] xl:w-[26rem] shrink-0 flex flex-col min-h-0">
              {renderEmployeeDirectory()}
            </div>

            <div className="card bg-base-100 shadow-xl flex min-h-0 flex-1 flex-col overflow-hidden">
              <div className="card-body flex min-h-0 flex-1 flex-col overflow-hidden">
                <div className="flex items-center justify-between gap-3 shrink-0">
                  <div className="min-w-0">
                    <h2 className="card-title">Employee Record</h2>
                    {selectedEmployeeName && selectedEmployeeId ? (
                      <p className="text-sm text-base-content/70 truncate">{selectedEmployeeName}</p>
                    ) : null}
                  </div>
                  <button className={`btn btn-primary btn-sm shrink-0 ${savingEmployee ? 'btn-disabled' : ''}`} onClick={handleSaveEmployee}>
                    <FontAwesomeIcon icon={faSave} className="mr-2" />
                    {savingEmployee ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
                {employeeMessage && <div className="alert alert-success mt-2 shrink-0"><span>{employeeMessage}</span></div>}
                {detailsError && <div className="alert alert-error mt-2 shrink-0"><span>{detailsError}</span></div>}

                <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain mt-2">
                {!selectedEmployeeId ? (
                  <div className="py-8 text-base-content/70">Select an employee to view details.</div>
                ) : detailsLoading || !profileForm || !contactForm ? (
                  <div className="py-8 text-base-content/70">Loading employee details...</div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                    <div className="form-control w-full">
                      <label className="label py-1"><span className="label-text font-medium">Employee ID</span></label>
                      <input className="input input-bordered w-full" value={selectedEmployeeId} readOnly />
                    </div>
                    <div className="form-control w-full">
                      <label className="label py-1"><span className="label-text font-medium">Full Name</span></label>
                      <input className="input input-bordered w-full" value={contactForm.name || ''} onChange={(e) => setContactField('name', e.target.value)} />
                    </div>
                    <div className="form-control w-full">
                      <label className="label py-1"><span className="label-text font-medium">First Name</span></label>
                      <input className="input input-bordered w-full" value={profileForm.firstName || ''} onChange={(e) => setProfileField('firstName', e.target.value)} />
                    </div>
                    <div className="form-control w-full">
                      <label className="label py-1"><span className="label-text font-medium">Last Name</span></label>
                      <input className="input input-bordered w-full" value={profileForm.lastName || ''} onChange={(e) => setProfileField('lastName', e.target.value)} />
                    </div>
                    <div className="form-control w-full">
                      <label className="label py-1"><span className="label-text font-medium">Preferred Name</span></label>
                      <input
                        className="input input-bordered w-full"
                        value={profileForm.preferredName || ''}
                        onChange={(e) => {
                          setProfileField('preferredName', e.target.value)
                          setContactField('preferredName', e.target.value)
                        }}
                      />
                    </div>
                    <div className="form-control w-full">
                      <label className="label py-1"><span className="label-text font-medium">Position</span></label>
                      <PositionSelect
                        value={profileForm.position || ''}
                        positions={configuredPositions}
                        allowEmpty
                        emptyLabel="No position"
                        onChange={(position) => {
                          setProfileField('position', position)
                          setContactField('position', position)
                        }}
                      />
                    </div>
                    <div className="form-control w-full">
                      <label className="label py-1"><span className="label-text font-medium">Department</span></label>
                      <select
                        className="select select-bordered w-full"
                        value={normalizeDepartment(profileForm.department)}
                        onChange={(e) => {
                          setProfileField('department', e.target.value)
                          setContactField('department', e.target.value)
                        }}
                        aria-label="Department"
                      >
                        <option value="">Select department</option>
                        {DEPARTMENT_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-control w-full">
                      <label className="label py-1"><span className="label-text font-medium">Home ZIP code</span></label>
                      <input
                        className="input input-bordered w-full"
                        value={profileForm.zipCode || ''}
                        inputMode="numeric"
                        autoComplete="postal-code"
                        placeholder="84065"
                        onChange={(e) => setProfileField('zipCode', formatZipInput(e.target.value))}
                      />
                    </div>
                    <div className="form-control w-full">
                      <label className="label py-1"><span className="label-text font-medium">Supervisor</span></label>
                      <input
                        className="input input-bordered w-full"
                        value={profileForm.supervisor || ''}
                        onChange={(e) => {
                          setProfileField('supervisor', e.target.value)
                          setContactField('supervisor', e.target.value)
                        }}
                      />
                    </div>
                    <div className="form-control w-full">
                      <label className="label py-1"><span className="label-text font-medium">Hire Date</span></label>
                      <DaisyDatePicker
                        className="w-full"
                        value={(profileForm.hireDate || '').slice(0, 10)}
                        onChange={(value) => setProfileField('hireDate', value)}
                        aria-label="Hire date"
                      />
                    </div>
                    <div className="form-control w-full">
                      <label className="label py-1"><span className="label-text font-medium">Pay Type</span></label>
                      <select
                        className="select select-bordered w-full"
                        value={normalizePayType(profileForm.payType)}
                        onChange={(e) => setProfileField('payType', e.target.value)}
                      >
                        <option value="">Select pay type</option>
                        {PAY_TYPE_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-control w-full">
                      <label className="label py-1"><span className="label-text font-medium">Driver Status</span></label>
                      <select
                        className="select select-bordered w-full"
                        value={normalizeDriverStatus(profileForm.driverStatus)}
                        onChange={(e) => setProfileField('driverStatus', e.target.value)}
                      >
                        <option value="">Select status</option>
                        {DRIVER_STATUS_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-control w-full">
                      <label className="label py-1"><span className="label-text font-medium">License Type</span></label>
                      <select
                        className="select select-bordered w-full"
                        value={normalizeLicenseType(profileForm.driversLicenseType) || 'none'}
                        onChange={(e) => setProfileField('driversLicenseType', e.target.value)}
                      >
                        <option value="">Select license type</option>
                        {LICENSE_TYPE_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-control w-full">
                      <label className="label py-1"><span className="label-text font-medium">License Expiration</span></label>
                      <DaisyDatePicker
                        className="w-full"
                        value={(profileForm.driversLicenseExpirationDate || '').slice(0, 10)}
                        onChange={(value) => setProfileField('driversLicenseExpirationDate', value)}
                        aria-label="License expiration"
                      />
                    </div>
                    <div className="form-control w-full">
                      <label className="label py-1"><span className="label-text font-medium">DOT Medical Card Expiration</span></label>
                      <DaisyDatePicker
                        className="w-full"
                        value={(profileForm.dotMedicalCardExpirationDate || '').slice(0, 10)}
                        onChange={(value) => setProfileField('dotMedicalCardExpirationDate', value)}
                        aria-label="DOT medical card expiration"
                      />
                    </div>
                    <div className="form-control w-full">
                      <label className="label py-1"><span className="label-text font-medium">Personal Email</span></label>
                      <input
                        className="input input-bordered w-full"
                        value={contactForm.email || ''}
                        onChange={(e) => setContactField('email', e.target.value)}
                        onBlur={(e) => setContactField('email', normalizeEmail(e.target.value))}
                      />
                    </div>
                    <div className="form-control w-full">
                      <label className="label py-1"><span className="label-text font-medium">Personal Phone</span></label>
                      <input
                        className="input input-bordered w-full"
                        value={contactForm.phone || ''}
                        inputMode="numeric"
                        placeholder="(555) 123-4567"
                        onChange={(e) => setContactField('phone', formatPhoneInput(e.target.value))}
                      />
                    </div>
                    <div className="form-control w-full">
                      <label className="label py-1"><span className="label-text font-medium">Work Email</span></label>
                      <input
                        className="input input-bordered w-full"
                        value={contactForm.workEmail || ''}
                        onChange={(e) => setContactField('workEmail', e.target.value)}
                        onBlur={(e) => setContactField('workEmail', normalizeEmail(e.target.value))}
                      />
                    </div>
                    <div className="form-control w-full">
                      <label className="label py-1"><span className="label-text font-medium">Work Phone</span></label>
                      <input
                        className="input input-bordered w-full"
                        value={contactForm.workPhone || ''}
                        inputMode="numeric"
                        placeholder="(555) 123-4567"
                        onChange={(e) => setContactField('workPhone', formatPhoneInput(e.target.value))}
                      />
                    </div>
                  </div>
                )}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'certManager' && (
          <div className="flex min-h-0 flex-1 flex-col lg:flex-row gap-4 overflow-hidden">
            <div className="h-[min(55vh,32rem)] min-h-[18rem] lg:h-auto lg:min-h-0 lg:w-[22rem] xl:w-[26rem] shrink-0 flex flex-col min-h-0">
              {renderEmployeeDirectory()}
            </div>

            <div className="card bg-base-100 shadow-xl flex min-h-0 flex-1 flex-col overflow-hidden">
              <div className="card-body flex min-h-0 flex-1 flex-col overflow-hidden">
                <div className="flex items-center justify-between gap-3 shrink-0">
                  <div className="flex items-center gap-2 min-w-0">
                    <h2 className="card-title">Certification Matrix</h2>
                    <span className="text-sm text-base-content/70 truncate">{selectedEmployeeName || 'Select an employee'}</span>
                    {hasDirtyCerts && (
                      <span className="badge badge-warning badge-sm">Unsaved</span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <FontAwesomeIcon
                      icon={faCircleCheck}
                      className={
                        certSaveIndicator.kind === 'saved'
                          ? 'text-success'
                          : certSaveIndicator.kind === 'error'
                            ? 'text-error'
                            : certSaveIndicator.kind === 'saving'
                              ? 'text-base-content/60 animate-pulse'
                              : 'text-base-content/40'
                      }
                      title={certSaveIndicator.message || 'Idle'}
                    />
                    <button
                      className={`btn btn-primary btn-sm ${certsSaving || !hasDirtyCerts ? 'btn-disabled' : ''}`}
                      onClick={() => void handleSaveCerts()}
                      disabled={certsSaving || !hasDirtyCerts}
                    >
                      <FontAwesomeIcon icon={faSave} className="mr-2" />
                      {certsSaving ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </div>
                {certsError && <div className="alert alert-error mt-2 shrink-0"><span>{certsError}</span></div>}

                <div className="min-h-0 flex-1 overflow-auto overscroll-contain mt-2">
                {certsLoading ? (
                  <div className="py-8 text-base-content/70">Loading certifications...</div>
                ) : !selectedEmployeeId ? (
                  <div className="py-8 text-base-content/70">Select an employee to manage certifications.</div>
                ) : (
                  <div>
                    {certRows.length === 0 ? (
                      <div className="py-6 text-base-content/70">No certifications assigned to this employee yet.</div>
                    ) : (
                      <table className="table table-zebra">
                        <thead className="sticky top-0 z-10 bg-base-100">
                          <tr>
                            <th>Certification</th>
                            <th>Status</th>
                            <th>Expiration</th>
                            <th>Document</th>
                          </tr>
                        </thead>
                        <tbody>
                          {certRows.map((cert) => {
                            const draft = certDrafts[cert.type] || { expirationDate: '', inProgress: false }
                            const draftStatus = statusFromDraft(draft)
                            const rowDirty = isCertDraftDirty(cert, draft)
                            return (
                              <tr key={cert.type}>
                                <td>
                                  <div className="flex items-center gap-2">
                                    {certDisplayLabel(cert)}
                                    {rowDirty && <span className="badge badge-ghost badge-xs">edited</span>}
                                  </div>
                                </td>
                                <td>
                                  <div className="flex items-center gap-3 min-w-48">
                                    <span className={`badge badge-sm ${statusBadgeClass(draftStatus)}`}>
                                      {statusLabel(draftStatus)}
                                    </span>
                                    <label className="label cursor-pointer gap-2 py-0">
                                      <span className="label-text text-xs whitespace-nowrap">In Progress</span>
                                      <input
                                        type="checkbox"
                                        className="toggle toggle-sm toggle-warning"
                                        checked={draft.inProgress}
                                        disabled={certsSaving}
                                        onChange={(e) => {
                                          updateCertDraft(cert.type, { inProgress: e.target.checked })
                                        }}
                                      />
                                    </label>
                                  </div>
                                </td>
                                <td>
                                  <DaisyDatePicker
                                    value={draft.expirationDate}
                                    disabled={certsSaving}
                                    aria-label={`${certLabel(cert.type, certCatalog)} expiration date`}
                                    onChange={(value) => {
                                      updateCertDraft(cert.type, { expirationDate: value })
                                    }}
                                  />
                                </td>
                                <td>
                                  <div className="inline-flex items-center gap-2 whitespace-nowrap">
                                    <button
                                      className="btn btn-ghost btn-sm btn-circle"
                                      onClick={() => handleViewCert(cert.type)}
                                      title="View certificate"
                                      aria-label="View certificate"
                                      disabled={!cert.filePath}
                                    >
                                      <FontAwesomeIcon icon={faEye} />
                                    </button>
                                    <label
                                      className={`btn btn-ghost btn-sm btn-circle ${certUploadingType ? 'btn-disabled' : ''}`}
                                      title="Upload certificate"
                                      aria-label="Upload certificate"
                                    >
                                      <FontAwesomeIcon icon={faFileUpload} />
                                      <input
                                        type="file"
                                        accept=".pdf,application/pdf"
                                        className="hidden"
                                        onChange={(e) => {
                                          const file = e.target.files?.[0]
                                          if (file) handleUploadCert(cert.type, file)
                                          e.target.value = ''
                                        }}
                                      />
                                    </label>
                                    <button
                                      className="btn btn-ghost btn-sm btn-circle text-error"
                                      onClick={() => handleDeleteCert(cert.type)}
                                      title="Remove certification from employee"
                                      aria-label="Remove certification from employee"
                                    >
                                      <FontAwesomeIcon icon={faUserMinus} />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            )
                          })}
                        </tbody>
                      </table>
                    )}
                    <div className="mt-3 flex items-center gap-2">
                      <select
                        className="select select-bordered select-sm min-w-60"
                        value={certTypeToAssign}
                        onChange={(e) => setCertTypeToAssign(e.target.value)}
                      >
                        <option value="">Add certification to employee...</option>
                        {unassignedCertTypes.map((item) => (
                          <option key={item.key} value={item.key}>{item.label}</option>
                        ))}
                      </select>
                      <button
                        className="btn btn-sm btn-primary"
                        disabled={!certTypeToAssign || certsSaving}
                        onClick={handleAssignCert}
                      >
                        <FontAwesomeIcon icon={faPlus} className="mr-2" />
                        Add
                      </button>
                    </div>
                  </div>
                )}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'hrTools' && (
          <div className="space-y-6">
            <HrPositionsCard />
            <HrCertTypesCard />
            <HrAdvanceCategoriesCard />
            <HrEeapCategoriesCard />
            <HrToolsTab />
          </div>
        )}
        {activeTab === 'advances' && (
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            <HrAdvancesTab />
          </div>
        )}

        {activeTab === 'payrollTools' && (
          <div className="space-y-6">
            <HrPaylocityTimeExportCard />
            <HrPayrollConfigCard />
          </div>
        )}
      </main>
      <Footer className={fillViewport ? '!mt-0' : undefined} />
    </div>
  )
}

export default HRPage
